import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Info, X, Clock, MailWarning } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import EarlyAccessRequestDialog from "./EarlyAccessRequestDialog";

/**
 * "Request Early Access" card for the Dashboard. Renders nothing
 * for admins and existing approved members — status is derived from the
 * user's ``is_private_test`` flag by ``GET /private-tester-requests/me``,
 * so every existing member automatically sees the button on first load
 * without any migration.
 */
export default function PrivateTesterAccessCard() {
  const { user } = useAuth();
  const isAdmin = (user?.roles || []).includes("admin");
  const isAlreadyPT = !!user?.is_private_test;
  const isVerified = !!user?.email_verified;

  const [state, setState] = useState({ loading: true, status: "none", request: null });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [verifyNoticeOpen, setVerifyNoticeOpen] = useState(false);
  const [resending, setResending] = useState(false);
  const infoBtnRef = useRef(null);

  useEffect(() => {
    if (isAdmin || isAlreadyPT || !user) {
      setState({ loading: false, status: isAlreadyPT ? "approved" : "none", request: null });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/private-tester-requests/me");
        if (!cancelled) setState({ loading: false, status: data.status, request: data.request });
      } catch {
        if (!cancelled) setState({ loading: false, status: "none", request: null });
      }
    })();
    return () => { cancelled = true; };
  }, [isAdmin, isAlreadyPT, user]);

  // Never rendered for admins, approved Early Access members, or ephemeral
  // demo visitors (they're already sandbox-scoped and can't file a
  // request from within the demo).
  if (!user || isAdmin || isAlreadyPT || user?.is_ephemeral || state.status === "approved") return null;

  const isPending = state.status === "pending";

  const onRequestClick = () => {
    if (!isVerified) {
      // Show the clear message + inline resend CTA that reuses the
      // existing /auth/verify-email/resend flow.
      setVerifyNoticeOpen(true);
      toast.error("Please verify your email before requesting Early Access.");
      return;
    }
    setDialogOpen(true);
  };

  const resend = async () => {
    setResending(true);
    try {
      await api.post("/auth/verify-email/resend", {});
      toast.success("Verification email sent — check your inbox in a moment.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "cooldown") {
        toast.info(detail.message || `Please wait ${detail.retry_after_seconds || 60}s before trying again.`);
      } else {
        toast.error(formatApiError(err));
      }
    } finally {
      setResending(false);
    }
  };

  return (
    <div
      className="mt-6 rounded-3xl border bg-card px-5 py-4 md:px-6 md:py-5 flex flex-col md:flex-row md:items-center md:flex-wrap gap-3 md:gap-4"
      data-testid="private-tester-access-card"
    >
      <div className="w-11 h-11 rounded-full bg-[hsl(var(--sb-forest))] grid place-items-center shrink-0 text-[hsl(42_60%_96%)]">
        <ShieldCheck size={20} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="font-serif text-lg md:text-xl">Early Access</h3>
          <div className="relative">
            <button
              ref={infoBtnRef}
              type="button"
              onClick={() => setInfoOpen((v) => !v)}
              aria-label="What is Early Access?"
              className="inline-flex items-center justify-center w-6 h-6 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              data-testid="private-tester-info-btn"
            >
              <Info size={14} />
            </button>
            {infoOpen && (
              <InfoPopover onClose={() => setInfoOpen(false)} />
            )}
          </div>
        </div>
        {isPending ? (
          <p className="mt-1 text-sm text-muted-foreground leading-relaxed">
            Thanks — your Early Access request is in the queue. We'll let you know as soon as it's reviewed.
          </p>
        ) : (
          <div className="mt-1 space-y-2">
            <p className="text-sm text-foreground/85 leading-relaxed">
              Your profile is not visible to other members yet. Request free Early Access to:
            </p>
            <ul className="text-sm text-foreground/85 leading-relaxed list-disc pl-5 space-y-0.5">
              <li>Make your profile discoverable</li>
              <li>View and post real projects</li>
              <li>Connect with authors and beta readers</li>
              <li>Use Coffee Chats and messaging</li>
            </ul>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Last Bookmark is still in beta, so you may occasionally be asked for feedback.
            </p>
          </div>
        )}
      </div>
      <div className="shrink-0 self-start md:self-center">
        {isPending ? (
          <span
            className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold bg-[hsl(var(--sb-gold-2))] text-[hsl(150_28%_10%)] border border-[hsl(var(--sb-gold-2))] shadow-sm"
            data-testid="private-tester-pending-pill"
          >
            <Clock size={14} /> Request Pending
          </span>
        ) : (
          <button
            type="button"
            onClick={onRequestClick}
            className="btn-primary whitespace-nowrap"
            data-testid="private-tester-request-btn"
          >
            Request Early Access
          </button>
        )}
      </div>

      {verifyNoticeOpen && !isVerified && !isPending && (
        <div
          className="w-full md:mt-3 md:basis-full rounded-xl border border-[hsl(var(--sb-clay))]/40 bg-[hsl(var(--sb-clay))]/10 px-4 py-3 flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4"
          role="status"
          data-testid="private-tester-verify-notice"
        >
          <div className="flex items-start gap-2 flex-1 min-w-0">
            <MailWarning size={16} className="mt-0.5 shrink-0 text-[hsl(var(--sb-clay))]" />
            <p className="text-sm text-foreground leading-snug">
              <span className="font-semibold">Please verify your email</span> before requesting Early Access. We sent a verification link to <span className="font-mono">{user.email}</span>.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={resend}
              disabled={resending}
              className="text-sm font-semibold underline-offset-4 hover:underline text-[hsl(var(--sb-clay))] disabled:opacity-60 whitespace-nowrap"
              data-testid="private-tester-verify-resend-btn"
            >
              {resending ? "Sending…" : "Resend verification email"}
            </button>
            <button
              type="button"
              onClick={() => setVerifyNoticeOpen(false)}
              className="rounded-full p-1 text-muted-foreground hover:bg-muted"
              aria-label="Dismiss"
              data-testid="private-tester-verify-dismiss"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      )}

      {dialogOpen && (
        <EarlyAccessRequestDialog
          onClose={() => setDialogOpen(false)}
          onSubmitted={(req) => {
            setState({ loading: false, status: "pending", request: req });
            setDialogOpen(false);
          }}
        />
      )}
    </div>
  );
}


function InfoPopover({ onClose }) {
  return (
    <>
      <button
        type="button"
        onClick={onClose}
        aria-hidden
        tabIndex={-1}
        className="fixed inset-0 z-40 cursor-default"
        style={{ background: "transparent" }}
      />
      <div
        className="absolute left-0 sm:left-auto sm:right-0 top-8 z-50 w-[calc(100vw-3rem)] sm:w-80 rounded-2xl border bg-card shadow-lg p-4 text-sm"
        role="dialog"
        aria-label="What is Early Access?"
        data-testid="private-tester-info-popover"
      >
        <h4 className="font-serif text-base mb-1">What is Early Access?</h4>
        <p className="text-muted-foreground leading-relaxed">
          Early Access members can use the real Last Bookmark community
          during beta — post and view real projects, apply, message
          authors and beta readers, and try Coffee Chats.
        </p>
        <p className="mt-2 text-muted-foreground leading-relaxed">
          Access is free and manually approved while Last Bookmark is
          still in beta. You may occasionally be asked for feedback to
          help shape the platform.
        </p>
      </div>
    </>
  );
}



